<script setup>

import {computed, onMounted, ref} from "vue";
import {useAdminStore} from "@/store/admin";
import {useAuthStore} from "@/store/auth";
import {useToast} from "vue-toast-notification";
import MainPreloader from "@/components/shared/MainPreloader.vue";
import EmptyCard from "@/components/shared/EmptyCard.vue";
import ActionButton from "@/components/ui/ActionButton.vue";
import AdminModal from "@/components/AdminView/AdminModal.vue";

const adminStore = useAdminStore();
const authStore = useAuthStore();
const toast = useToast();

const report = computed(() => adminStore.systemStatus);
const currentUserId = computed(() => authStore.userId);

const sessionsModalVisible = ref(false);
const activeSessions = ref([]);
const sessionsLoading = ref(false);

const sessionDetailsModalVisible = ref(false);
const sessionDetails = ref([]);
const sessionDetailsLoading = ref(false);

async function openSessionDetailsModal() {
    sessionDetailsModalVisible.value = true;
    sessionDetailsLoading.value = true;
    sessionDetails.value = await adminStore.loadSessionDetails();
    if (adminStore.error) {
        toast.error(adminStore.error);
    }
    sessionDetailsLoading.value = false;
}

function closeSessionDetailsModal() {
    sessionDetailsModalVisible.value = false;
}

async function openSessionsModal() {
    sessionsModalVisible.value = true;
    sessionsLoading.value = true;
    activeSessions.value = await adminStore.loadActiveSessions();
    if (adminStore.error) {
        toast.error(adminStore.error);
    }
    sessionsLoading.value = false;
}

function closeSessionsModal() {
    sessionsModalVisible.value = false;
}

async function closeAllSessions() {
    if (!confirm("Закрыть все открытые сессии других пользователей? Ваша текущая сессия сохранится.")) return;
    const result = await adminStore.revokeAllSessions();
    if (adminStore.error) {
        toast.error(adminStore.error);
        return;
    }
    toast.success(`Закрыто сессий: ${result?.revoked ?? 0}`);
    await refreshSessions();
}

async function closeUserSessions(item) {
    if (!confirm(`Закрыть все сессии пользователя «${item.user_name}»?`)) return;
    const result = await adminStore.revokeUserSessions(item.user_id);
    if (adminStore.error) {
        toast.error(adminStore.error);
        return;
    }
    toast.success(`Закрыто сессий: ${result?.revoked ?? 0}`);
    await refreshSessions();
}

async function blockUser(item) {
    if (!confirm(`Удалить пользователя «${item.user_name}» с блокировкой? Учётная запись будет деактивирована, все сессии закрыты, повторный вход станет невозможен.`)) return;
    const ok = await adminStore.blockUser(item.user_id);
    if (!ok) {
        toast.error(adminStore.error || "Не удалось заблокировать пользователя");
        return;
    }
    toast.success("Пользователь заблокирован, все сессии закрыты");
    await refreshSessions();
}

async function refreshSessions() {
    await adminStore.loadSystemStatus();
    if (sessionsModalVisible.value) {
        activeSessions.value = await adminStore.loadActiveSessions();
    }
    if (sessionDetailsModalVisible.value) {
        sessionDetails.value = await adminStore.loadSessionDetails();
    }
}

function humanBytes(value) {
    if (value === null || value === undefined) return "—";
    const units = ["Б", "КиБ", "МиБ", "ГиБ", "ТиБ"];
    let size = Number(value);
    let index = 0;
    while (size >= 1024 && index < units.length - 1) {
        size /= 1024;
        index += 1;
    }
    const digits = index > 0 && size < 10 ? 1 : 0;
    return `${size.toFixed(digits)} ${units[index]}`;
}

function humanPercent(value) {
    if (value === null || value === undefined) return "—";
    return `${Number(value).toFixed(1)}%`;
}

function humanDuration(seconds) {
    if (seconds === null || seconds === undefined) return "—";
    const total = Math.floor(Number(seconds));
    const days = Math.floor(total / 86400);
    const hours = Math.floor((total % 86400) / 3600);
    const minutes = Math.floor((total % 3600) / 60);
    if (days > 0) return `${days} д ${hours} ч`;
    if (hours > 0) return `${hours} ч ${minutes} мин`;
    if (minutes > 0) return `${minutes} мин`;
    return `${total} с`;
}

function statusLabel(status) {
    return {ok: "Норма", warning: "Внимание", critical: "Критично"}[status] || "Норма";
}

function statusClass(status) {
    return `sysStatus sysStatus--${status || "ok"}`;
}

function rowStatusClass(status) {
    return status && status !== "ok" ? `sysRow--${status}` : "";
}

// cacheHitTooltip — подробное пояснение о cache hit ratio для всплывающей подсказки.
const cacheHitTooltip = computed(() => {
    const pct = report.value?.database?.cache_hit_percent;
    const line = (num, text) => `${num}. ${text}<br>`;
    return (
        "<b>Cache hit ratio</b> — доля обращений к блокам данных, выполненных из оперативной памяти " +
        "(shared_buffers и кэш ОС), а не с диска.<br><br>" +
        "<b>Формула:</b> blks_hit / (blks_hit + blks_read). " +
        `Текущее значение: <b>${humanPercent(pct)}</b>.<br><br>` +
        "<b>Почему значение ниже нормы (&lt; 90%)?</b><br>" +
        line(1, "Малый shared_buffers — рабочий набор БД не помещается в память СУБД (рекомендуется ~25% ОЗУ).") +
        line(2, "Сервер недавно перезапускался: кэш холодный, счётчики pg_stat_database суммируются с момента старта — через 1–2 дня работы значение обычно растёт.") +
        line(3, "Много последовательных сканирований больших таблиц без подходящих индексов (смотрите колонку Seq/Idx сканирований в отчёте по таблицам).") +
        line(4, "Накоплены мёртвые строки — индексы читают лишние страницы (выполните VACUUM ANALYZE на проблемных таблицах).") +
        "<br><b>Что проверить:</b><br>" +
        line(1, "shared_buffers и объём ОЗУ сервера (SHOW shared_buffers).") +
        line(2, "Крупнейшие таблицы с высокими seq_scan — добавить индексы или переписать запросы.") +
        line(3, "Динамику: сравнить значение сейчас и спустя время после перезапуска СУБД.")
    );
});

async function load() {
    await adminStore.loadSystemStatus();
    if (adminStore.error) {
        toast.error(adminStore.error);
    }
}

onMounted(load);

</script>

<template>
  <div class="adminSystem">
    <MainPreloader
        v-if="adminStore.loading"
        position="fixed"
        :size="80"
        :stroke="3"
    />

    <div class="adminSystem__toolbar">
      <div class="adminSystem__toolbarTitle">Состояние системы</div>
      <ActionButton @buttonClick="load">
        <template #caption>Обновить</template>
      </ActionButton>
    </div>

    <EmptyCard v-if="!report && !adminStore.loading"/>

    <template v-else-if="report">
      <div class="sysStatus sysStatus--overall" :class="statusClass(report.status)">
        <span class="sysStatus__dot"></span>
        <span class="sysStatus__text">Общее состояние: {{ statusLabel(report.status) }}</span>
        <span class="sysStatus__time">Обновлено: {{ new Date(report.generated_at).toLocaleString() }}</span>
      </div>

      <div v-if="report.alerts?.length" class="adminSystem__alerts">
        <div
            v-for="(alert, idx) in report.alerts"
            :key="idx"
            class="sysStatus"
            :class="statusClass(alert.level)"
        >
          <span class="sysStatus__dot"></span>
          <span class="sysStatus__text">{{ alert.message }}</span>
        </div>
      </div>

      <h3 class="adminSystem__heading">Сессии, пользователи и таблицы</h3>
      <div class="adminSystem__grid">
        <div class="adminSystem__card adminSystem__card--clickable" @click="openSessionDetailsModal">
          <div class="adminSystem__cardTitle">Открытые сессии</div>
          <div class="adminSystem__metric">{{ report.sessions.active_sessions }}</div>
          <div class="adminSystem__muted">За {{ report.sessions.recent_window_days }} дн: {{ report.sessions.recent_sessions }}</div>
          <div class="adminSystem__muted">Кликните для просмотра списка</div>
        </div>
        <div class="adminSystem__card adminSystem__card--clickable" @click="openSessionsModal">
          <div class="adminSystem__cardTitle">Работающие пользователи</div>
          <div class="adminSystem__metric">{{ report.sessions.active_users }}</div>
          <div class="adminSystem__muted">Всего пользователей: {{ report.sessions.registered_users }}</div>
          <div class="adminSystem__muted">Членов клуба: {{ report.sessions.club_members }}</div>
          <div class="adminSystem__muted">Заказывали: {{ report.sessions.users_with_orders }}</div>
          <div class="adminSystem__muted">Покупали: {{ report.sessions.users_with_paid_orders }}</div>
          <div class="adminSystem__muted">Кликните для просмотра списка</div>
        </div>
        <div class="adminSystem__card">
          <div class="adminSystem__cardTitle">Таблицы</div>
          <div class="adminSystem__tableSummary">
            <div><span>Заказы</span><strong>{{ report.project_tables.orders }}</strong></div>
            <div><span>Новости</span><strong>{{ report.project_tables.news }}</strong></div>
            <div><span>Рассылки</span><strong>{{ report.project_tables.mailings }}</strong></div>
            <div><span>Товары</span><strong>{{ report.project_tables.products }}</strong></div>
            <div><span>Прайс-лист</span><strong>{{ report.project_tables.price_list_items }}</strong></div>
            <div><span>Монеты</span><strong>{{ report.project_tables.coins }}</strong></div>
          </div>
        </div>
      </div>

      <h3 class="adminSystem__heading">Операционная система</h3>
      <div class="adminSystem__grid">
        <div class="adminSystem__card" :class="statusClass(report.os.cpu.status)">
          <div class="adminSystem__cardTitle">Процессор</div>
          <div class="adminSystem__metric">{{ humanPercent(report.os.cpu.used_percent) }}</div>
          <div class="adminSystem__muted">Ядер: {{ report.os.cpu.count || '—' }}</div>
          <div v-if="report.os.cpu.note" class="adminSystem__note">{{ report.os.cpu.note }}</div>
        </div>
        <div class="adminSystem__card" :class="statusClass(report.os.memory.status)">
          <div class="adminSystem__cardTitle">Оперативная память</div>
          <div class="adminSystem__metric">{{ humanPercent(report.os.memory.used_percent) }}</div>
          <div class="adminSystem__muted">
            {{ humanBytes(report.os.memory.used) }} / {{ humanBytes(report.os.memory.total) }},
            свободно {{ humanBytes(report.os.memory.available) }}
          </div>
          <div v-if="report.os.memory.note" class="adminSystem__note">{{ report.os.memory.note }}</div>
        </div>
        <div class="adminSystem__card" :class="statusClass(report.os.load.status)">
          <div class="adminSystem__cardTitle">Средняя загрузка</div>
          <div class="adminSystem__metric">
            {{ report.os.load.load1.toFixed(2) }} / {{ report.os.load.load5.toFixed(2) }} / {{ report.os.load.load15.toFixed(2) }}
          </div>
          <div v-if="report.os.load.note" class="adminSystem__note">{{ report.os.load.note }}</div>
        </div>
        <div class="adminSystem__card">
          <div class="adminSystem__cardTitle">Узел</div>
          <div class="adminSystem__value">{{ report.os.hostname || '—' }}</div>
          <div class="adminSystem__muted">
            {{ report.os.platform }} {{ report.os.platform_version }} ({{ report.os.kernel_version }})
          </div>
          <div class="adminSystem__muted">Uptime: {{ humanDuration(report.os.uptime_seconds) }}</div>
        </div>
      </div>

      <h3 class="adminSystem__heading">Диски</h3>
      <table class="adminTable">
        <thead>
        <tr>
          <th>Точка монтирования</th>
          <th>ФС</th>
          <th>Всего</th>
          <th>Занято</th>
          <th>Свободно</th>
          <th>Использовано</th>
        </tr>
        </thead>
        <tbody>
        <tr v-for="item in report.os.disks" :key="item.mount" :class="rowStatusClass(item.status)">
          <td>{{ item.mount }}</td>
          <td>{{ item.fstype }}</td>
          <td>{{ humanBytes(item.total) }}</td>
          <td>{{ humanBytes(item.used) }}</td>
          <td>{{ humanBytes(item.free) }}</td>
          <td>{{ humanPercent(item.used_percent) }} <span v-if="item.note" class="adminSystem__note">{{ item.note }}</span></td>
        </tr>
        </tbody>
      </table>

      <h3 class="adminSystem__heading">СУБД</h3>
      <div class="adminSystem__grid">
        <div class="adminSystem__card">
          <div class="adminSystem__cardTitle">Версия PostgreSQL</div>
          <div class="adminSystem__value">{{ report.database.version }}</div>
          <div class="adminSystem__muted">Uptime: {{ humanDuration(report.database.uptime_seconds) }}</div>
        </div>
        <div class="adminSystem__card">
          <div class="adminSystem__cardTitle">Размер базы</div>
          <div class="adminSystem__metric">{{ humanBytes(report.database.size_bytes) }}</div>
        </div>
        <div class="adminSystem__card" :class="statusClass(report.database.status)">
          <div class="adminSystem__cardTitle">Подключения</div>
          <div class="adminSystem__metric">
            {{ report.database.connections }} / {{ report.database.max_connections }}
            <span class="adminSystem__muted">({{ humanPercent(report.database.connections_percent) }})</span>
          </div>
          <div class="adminSystem__muted">
            active: {{ report.database.active_connections }},
            idle: {{ report.database.idle_connections }},
            idle in tx: {{ report.database.idle_in_transaction }}
          </div>
        </div>
        <div class="adminSystem__card">
          <div class="adminSystem__cardTitle">Cache hit ratio
            <span class="adminSystem__info" v-tooltip="cacheHitTooltip">&#9432;</span>
          </div>
          <div class="adminSystem__metric">{{ humanPercent(report.database.cache_hit_percent) }}</div>
          <div class="adminSystem__muted">Deadlocks: {{ report.database.deadlocks }}, блокировок: {{ report.database.blocked_queries }}</div>
        </div>
        <div class="adminSystem__card">
          <div class="adminSystem__cardTitle">Транзакции</div>
          <div class="adminSystem__muted">Commit: {{ report.database.xact_commit }}</div>
          <div class="adminSystem__muted">Rollback: {{ report.database.xact_rollback }}</div>
          <div class="adminSystem__muted">Temp: {{ humanBytes(report.database.temp_bytes) }}</div>
        </div>
        <div class="adminSystem__card" :class="statusClass(report.database.status)">
          <div class="adminSystem__cardTitle">Долгий запрос</div>
          <div class="adminSystem__metric">{{ humanDuration(report.database.longest_query_seconds) }}</div>
          <div v-if="report.database.note" class="adminSystem__note">{{ report.database.note }}</div>
        </div>
      </div>

      <h3 class="adminSystem__heading">Таблицы ({{ report.tables.length }})</h3>
      <table class="adminTable">
        <thead>
        <tr>
          <th>Таблица</th>
          <th>Размер</th>
          <th>Живых строк</th>
          <th>Мёртвых строк</th>
          <th>Seq / Idx сканирований</th>
          <th>Последний autovacuum</th>
        </tr>
        </thead>
        <tbody>
        <tr v-for="item in report.tables" :key="item.schema + '.' + item.name" :class="rowStatusClass(item.status)">
          <td>{{ item.schema }}.{{ item.name }}</td>
          <td>{{ humanBytes(item.size_bytes) }}</td>
          <td>{{ item.live_tuples }}</td>
          <td>
            {{ item.dead_tuples }} <span class="adminSystem__muted">({{ humanPercent(item.dead_percent) }})</span>
            <div class="adminSystem__muted">порог autovacuum: {{ item.autovacuum_vacuum_threshold.toFixed(1) }}</div>
          </td>
          <td>{{ item.seq_scan }} / {{ item.idx_scan }}</td>
          <td>{{ item.last_autovacuum || '—' }} <span v-if="item.note" class="adminSystem__note">{{ item.note }}</span></td>
        </tr>
        </tbody>
      </table>

      <h3 class="adminSystem__heading">Индексы ({{ report.indexes.length }})</h3>
      <table class="adminTable">
        <thead>
        <tr>
          <th>Индекс</th>
          <th>Таблица</th>
          <th>Размер</th>
          <th>Сканирований</th>
          <th>Тип</th>
        </tr>
        </thead>
        <tbody>
        <tr v-for="item in report.indexes" :key="item.schema + '.' + item.name" :class="rowStatusClass(item.status)">
          <td>{{ item.name }}</td>
          <td>{{ item.table }}</td>
          <td>{{ humanBytes(item.size_bytes) }}</td>
          <td>{{ item.idx_scan }} <span v-if="item.note" class="adminSystem__note">{{ item.note }}</span></td>
          <td>{{ item.is_primary ? 'PK' : (item.is_unique ? 'unique' : 'index') }}</td>
        </tr>
        </tbody>
      </table>
    </template>

    <AdminModal :visible="sessionsModalVisible" title="Работающие пользователи" @close="closeSessionsModal">
      <MainPreloader v-if="sessionsLoading" :size="40" :stroke="3"/>
      <div v-else-if="!activeSessions.length" class="adminSystem__muted">Открытых сессий нет.</div>
      <table v-else class="adminTable">
        <thead>
        <tr>
          <th>Имя</th>
          <th>Действия</th>
          <th>Email</th>
          <th>IP</th>
          <th>Сессий</th>
          <th>Сессия до</th>
        </tr>
        </thead>
        <tbody>
        <tr v-for="(item, idx) in activeSessions" :key="idx">
          <td>{{ item.user_name }}</td>
          <td class="adminSystem__actions">
            <template v-if="item.user_id !== currentUserId">
              <button
                  class="adminSystem__actionButton adminSystem__actionButton--danger"
                  title="Закрыть все сессии пользователя"
                  @click="closeUserSessions(item)"
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                  <path d="M16 17l5-5-5-5M21 12H9" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                </svg>
              </button>
              <button
                  class="adminSystem__actionButton adminSystem__actionButton--danger"
                  title="Удалить с блокировкой (запретить вход)"
                  @click="blockUser(item)"
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M18 6L6 18M6 6l12 12" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                </svg>
              </button>
            </template>
            <span v-else class="adminSystem__muted">Это вы</span>
          </td>
          <td>{{ item.email || '—' }}</td>
          <td>{{ item.ips || '—' }}</td>
          <td>{{ item.sessions }}</td>
          <td>{{ new Date(item.expires_at).toLocaleString() }}</td>
        </tr>
        </tbody>
      </table>
    </AdminModal>

    <AdminModal :visible="sessionDetailsModalVisible" title="Открытые сессии" @close="closeSessionDetailsModal">
      <template #header>
        <ActionButton
            style="background-color: var(--cd-danger); color: #ffffff;"
            @buttonClick="closeAllSessions"
        >
          <template #caption>Закрыть все сессии</template>
        </ActionButton>
      </template>
      <MainPreloader v-if="sessionDetailsLoading" :size="40" :stroke="3"/>
      <div v-else-if="!sessionDetails.length" class="adminSystem__muted">Открытых сессий нет.</div>
      <table v-else class="adminTable">
        <thead>
        <tr>
          <th>Имя</th>
          <th>Email</th>
          <th>IP</th>
          <th>Сессия до</th>
        </tr>
        </thead>
        <tbody>
        <tr v-for="item in sessionDetails" :key="item.user_name + item.expires_at">
          <td>{{ item.user_name }}</td>
          <td>{{ item.email || '—' }}</td>
          <td>{{ item.ip || '—' }}</td>
          <td>{{ new Date(item.expires_at).toLocaleString() }}</td>
        </tr>
        </tbody>
      </table>
    </AdminModal>
  </div>
</template>

<style scoped lang="scss">
.adminSystem {
  text-align: left;

  &__toolbar {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 16px;
    margin-bottom: 16px;
  }

  &__toolbarTitle {
    font-size: 20px;
    font-weight: 600;
    color: var(--cd-text);
  }

  &__actions {
    vertical-align: middle;
    white-space: nowrap;
  }

  &__actionButton {
    background: transparent;
    border: 1px solid var(--cd-base-green);
    color: var(--cd-base-green);
    border-radius: 8px;
    padding: 6px;
    cursor: pointer;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    transition: all 0.3s;

    & + & {
      margin-left: 8px;
    }

    &:hover {
      background: var(--cd-base-green);
      color: #ffffff;
    }

    &--danger {
      border-color: var(--cd-danger);
      color: var(--cd-danger);

      &:hover {
        background: var(--cd-danger);
        color: #ffffff;
      }
    }
  }

  &__heading {
    margin: 24px 0 12px;
    font-size: 16px;
    color: var(--cd-text);
  }

  &__grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(220px, 1fr));
    gap: 12px;
  }

  &__card {
    padding: 16px;
    border-radius: 12px;
    background-color: var(--cd-card-bg);
    border: 1px solid transparent;

    &--clickable {
      cursor: pointer;
      transition: border-color 0.3s;

      &:hover {
        border-color: var(--cd-base-green);
      }
    }
  }

  &__cardTitle {
    font-size: 13px;
    color: var(--cd-text-muted);
    margin-bottom: 6px;
  }

  &__metric {
    font-size: 22px;
    font-weight: 600;
    color: var(--cd-text);
  }

  &__tableSummary {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 6px 12px;
    font-size: 12px;
    color: var(--cd-text-muted);

    div {
      display: flex;
      justify-content: space-between;
      gap: 8px;
    }

    strong {
      color: var(--cd-text);
    }
  }

  &__value {
    font-size: 15px;
    color: var(--cd-text);
    word-break: break-word;
  }

  &__muted {
    font-size: 12px;
    color: var(--cd-text-muted);
    margin-top: 4px;
  }

  &__note {
    display: block;
    font-size: 12px;
    margin-top: 4px;
  }

  &__info {
    cursor: help;
    margin-left: 4px;
    color: var(--cd-text-muted);
    font-size: 14px;
  }

  &__alerts {
    display: flex;
    flex-direction: column;
    gap: 8px;
    margin-bottom: 8px;
  }
}

.sysStatus {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 16px;
  border-radius: 12px;
  border: 1px solid transparent;
  background-color: var(--cd-card-bg);

  &--overall {
    margin-bottom: 12px;
  }

  &__dot {
    width: 10px;
    height: 10px;
    border-radius: 50%;
    flex: none;
    background-color: currentColor;
  }

  &__text {
    color: var(--cd-text);
  }

  &__time {
    margin-left: auto;
    font-size: 12px;
    color: var(--cd-text-muted);
  }

  &--ok {
    color: #6FC062;
  }

  &--warning {
    color: #E0A800;
    border-color: #E0A800;
  }

  &--critical {
    color: #D9534F;
    border-color: #D9534F;
  }
}

tr.sysRow--warning td {
  background-color: rgba(224, 168, 0, 0.12);
}

tr.sysRow--critical td {
  background-color: rgba(217, 83, 79, 0.12);
}

.adminTable {
  @include respond-to('md') {
    display: block;
    overflow-x: auto;
  }
}
</style>
